import { useEffect } from 'react';
import type { UserDto } from '@pxc/shared';
import { useLevelUpStore } from '../stores/levelup.store.js';

/**
 * Raises the level-up animation when the account's level goes up.
 *
 * The server moves the level whenever a credit lifts lifetime earnings past a
 * line — a claimed shift, a task, the check-in, the combo, the wheel, a
 * friend's commission — and every one of those screens refetches the mine or
 * the profile after it. So this watches the level those two carry, rather
 * than every way of earning.
 *
 * The level last seen is kept per account on this device, and every climb
 * above it is celebrated — a level lost (the panel can raise the ladder) and
 * climbed back plays the jump again, by the owner's call. The first time a
 * device sees an account it only takes note: someone who reached level 5 last
 * week is not greeted with a jump today. Where storage is refused, the same
 * rule holds for the session.
 */
const memory = new Map<number, number>();
const key = (userId: number) => `pxc.level-seen.${userId}`;

function seenLevel(userId: number): number | null {
  try {
    const raw = window.localStorage.getItem(key(userId));
    if (raw !== null && Number.isFinite(Number(raw))) return Number(raw);
  } catch {
    /* storage refused: the session's memory stands in */
  }
  return memory.get(userId) ?? null;
}

function rememberLevel(userId: number, level: number): void {
  memory.set(userId, level);
  try {
    window.localStorage.setItem(key(userId), String(level));
  } catch {
    /* kept for the session only */
  }
}

/** Of two views of the account, the one further up the ladder is the newer. */
function freshest(...users: (UserDto | undefined)[]): UserDto | undefined {
  return users.reduce<UserDto | undefined>(
    (best, user) => (user && (!best || user.level > best.level) ? user : best),
    undefined,
  );
}

export function useLevelWatch(...views: (UserDto | undefined)[]): void {
  const user = freshest(...views);
  const userId = user?.id;
  const level = user?.level;
  const next = user?.nextLevelAtMpxc ?? null;

  useEffect(() => {
    if (userId === undefined || level === undefined) return;
    const seen = seenLevel(userId);
    // Down as well as up, so a level lost and climbed back is celebrated again.
    if (seen !== level) rememberLevel(userId, level);
    if (seen === null || level <= seen) return;
    useLevelUpStore.getState().show({ level, nextLevelAtMpxc: next });
  }, [userId, level, next]);
}
